import * as THREE from 'three';
import { PartOrientation } from '../../../contexts/LoadingPlanContext';
import {
  TRAILER_BOX_NAME,
  DEFAULT_VOXEL_SIZE_FT,
  VOXEL_BOUNDARY_TOLERANCE_FT,
  VOXEL_BOUNDARY_NUDGE_FT,
  ORIENTATION_STEP_COUNT_LONG,
  ORIENTATION_STEP_ANGLE_LONG_DEG,
  ORIENTATION_STEP_COUNT_SQUARE,
  ORIENTATION_STEP_ANGLE_SQUARE_DEG,
} from './editMode.constants';

/**
 * X/Y shift that moves a box whose min corner sits on (or a hair below) a voxel boundary just past it.
 * The engine floors the part's min corner to a voxel index, so float noise below a boundary puts the part in
 * the neighbour's voxel and the whole part is rejected as an obstacle.
 */
export function computeVoxelBoundaryNudge(box: THREE.Box3, voxelSize = DEFAULT_VOXEL_SIZE_FT): THREE.Vector3 {
  if (box.isEmpty()) return new THREE.Vector3(0, 0, 0);
  const axisShift = (min: number) => {
    const boundary = Math.round(min / voxelSize) * voxelSize;
    return Math.abs(min - boundary) <= VOXEL_BOUNDARY_TOLERANCE_FT ? boundary + VOXEL_BOUNDARY_NUDGE_FT - min : 0;
  };
  return new THREE.Vector3(axisShift(box.min.x), axisShift(box.min.y), 0);
}

// Truck bounds are derived from the trailer box mesh already built from
// nestingOutput.truck.dimensions, so axes/units stay in sync with the scene.
export function getTruckBounds(scene: THREE.Scene): THREE.Box3 | null {
  const trailerBox = scene.children.find((child) => child.name === TRAILER_BOX_NAME);
  if (!trailerBox) {
    return null;
  }

  return new THREE.Box3().setFromObject(trailerBox);
}

/**
 * Returns truck boundaries with bounds.min.z raised to the top of any bottom foundation
 * support objects (dunnage, pallets), ensuring steel parts can never be moved below the foundation.
 */
export function getTruckBoundsWithFoundation(scene: THREE.Scene): THREE.Box3 | null {
  const truckBounds = getTruckBounds(scene);
  if (!truckBounds) {
    return null;
  }

  let maxFoundationTopZ = truckBounds.min.z;

  scene.traverse((child: THREE.Object3D) => {
    if (child.name !== TRAILER_BOX_NAME && child.visible) {
      const uuidBase = child.uuid?.includes('#') ? child.uuid.split('#')[0] : (child.uuid || '');
      const nameBase = child.name?.includes('#') ? child.name.split('#')[0] : (child.name || '').replace(/\.stl$/i, '');
      const isSupport = uuidBase === 'dunnage' || uuidBase === 'pallet' || nameBase === 'dunnage' || nameBase === 'pallet';

      if (isSupport) {
        const supportBox = new THREE.Box3().setFromObject(child);
        if (!supportBox.isEmpty()) {
          if (supportBox.min.z <= truckBounds.min.z + 0.2) {
            const floorFoundationTop = Math.min(supportBox.max.z, truckBounds.min.z + 1.0);
            if (floorFoundationTop > maxFoundationTopZ) {
              maxFoundationTopZ = floorFoundationTop;
            }
          }
        }
      }
    }
  });

  const effectiveBounds = truckBounds.clone();
  effectiveBounds.min.z = maxFoundationTopZ;
  return effectiveBounds;
}


const AXES = ['x', 'y', 'z'] as const;

/**
 * Clamps a pivot position so that a part's bounding box (expressed as its
 * min/max offsets from the pivot) stays fully inside the given bounds.
 */
export function clampPivotToBounds(
  position: THREE.Vector3,
  minOffset: THREE.Vector3,
  maxOffset: THREE.Vector3,
  bounds: THREE.Box3,
): THREE.Vector3 {
  const clamped = position.clone();
  AXES.forEach((axis) => {
    const min = bounds.min[axis] - minOffset[axis];
    const max = bounds.max[axis] - maxOffset[axis];
    // If the part doesn't fit on this axis, leave it as-is rather than forcing an invalid range.
    if (min <= max) {
      clamped[axis] = THREE.MathUtils.clamp(clamped[axis], min, max);
    }
  });
  return clamped;
}

export function measureBox(object: THREE.Object3D, matrix: THREE.Matrix4): THREE.Box3 {
  const previousMatrix = object.matrix.clone();
  const previousAutoUpdate = object.matrixAutoUpdate;

  object.matrixAutoUpdate = false;
  object.matrix.copy(matrix);
  object.matrixWorldNeedsUpdate = true;
  object.updateMatrixWorld(true);
  // Precise: the AABB of a rotated local AABB is far taller than the part for parts with skewed local axes.
  const box = new THREE.Box3().setFromObject(object, true);

  object.matrix.copy(previousMatrix);
  object.matrixAutoUpdate = previousAutoUpdate;
  object.matrixWorldNeedsUpdate = true;
  object.updateMatrixWorld(true);

  return box;
}

const axesByDescendingSize = (size: THREE.Vector3): number[] =>
  [0, 1, 2].sort((a, b) => size.getComponent(b) - size.getComponent(a));

/** Rotates part bounding-box axes to align with the slot's orientation. */
export function computeAxisAlignmentRotation(
  partSize: THREE.Vector3,
  slotSize: THREE.Vector3,
): THREE.Matrix4 {
  const partAxes = axesByDescendingSize(partSize);
  const slotAxes = axesByDescendingSize(slotSize);

  const cells = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  partAxes.forEach((partAxis, rank) => {
    cells[slotAxes[rank] * 3 + partAxis] = 1;
  });

  const rotation = new THREE.Matrix4().set(
    cells[0], cells[1], cells[2], 0,
    cells[3], cells[4], cells[5], 0,
    cells[6], cells[7], cells[8], 0,
    0, 0, 0, 1,
  );

  // An odd axis permutation mirrors the part; flipping one axis restores a proper rotation.
  if (rotation.determinant() < 0) {
    cells[slotAxes[2] * 3 + partAxes[2]] = -1;
    rotation.set(
      cells[0], cells[1], cells[2], 0,
      cells[3], cells[4], cells[5], 0,
      cells[6], cells[7], cells[8], 0,
      0, 0, 0, 1,
    );
  }

  return rotation;
}

/** Builds placement matrix to drop a replacement part into a target slot. */
export function computeSlotPlacementMatrix(
  replacement: THREE.Object3D,
  baseMatrix: THREE.Matrix4,
  slotBox: THREE.Box3,
  matchSlotAxes = false,
): THREE.Matrix4 {
  let placement = baseMatrix.clone();
  let replacementBox = measureBox(replacement, placement);

  if (replacementBox.isEmpty() || slotBox.isEmpty()) {
    return placement;
  }

  if (matchSlotAxes) {
    const rotation = computeAxisAlignmentRotation(
      replacementBox.getSize(new THREE.Vector3()),
      slotBox.getSize(new THREE.Vector3()),
    );
    const center = replacementBox.getCenter(new THREE.Vector3());

    placement = new THREE.Matrix4()
      .makeTranslation(center.x, center.y, center.z)
      .multiply(rotation)
      .multiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z))
      .multiply(placement);

    replacementBox = measureBox(replacement, placement);
  }

  const slotCenter = slotBox.getCenter(new THREE.Vector3());
  const replacementCenter = replacementBox.getCenter(new THREE.Vector3());

  // Center replacement on the slot.
  const delta = new THREE.Vector3(
    slotCenter.x - replacementCenter.x,
    slotCenter.y - replacementCenter.y,
    slotCenter.z - replacementCenter.z,
  );

  return placement.premultiply(new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z));
}

/** Clamps slot placement so the part fits inside the truck boundaries. */
export function fitPlacementToTruckBounds(
  object: THREE.Object3D,
  placement: THREE.Matrix4,
  truckBounds: THREE.Box3 | null,
): THREE.Matrix4 {
  if (!truckBounds || truckBounds.isEmpty()) {
    return placement;
  }

  const box = measureBox(object, placement);
  if (box.isEmpty()) {
    return placement;
  }

  const center = box.getCenter(new THREE.Vector3());
  const clamped = clampPivotToBounds(
    center,
    box.min.clone().sub(center),
    box.max.clone().sub(center),
    truckBounds,
  );

  const delta = clamped.sub(center);
  if (delta.lengthSq() === 0) {
    return placement;
  }

  return placement.clone().premultiply(new THREE.Matrix4().makeTranslation(delta.x, delta.y, delta.z));
}

/**
 * Converts a Three.js 4x4 matrix (internal column-major .elements array)
 * into a 4x4 row-major number[][] array required by the nesting algorithm.
 */
export function fromMatrix4(matrix: THREE.Matrix4): number[][] {
  const e = matrix.elements;
  return [
    [e[0], e[4], e[8], e[12]],
    [e[1], e[5], e[9], e[13]],
    [e[2], e[6], e[10], e[14]],
    [e[3], e[7], e[11], e[15]],
  ];
}

/**
 * Converts a 4x4 row-major number[][] array (as used by the nesting algorithm)
 * back into a Three.js Matrix4 (the exact inverse of fromMatrix4).
 */
export function toMatrix4(m: number[][]): THREE.Matrix4 {
  return new THREE.Matrix4().set(
    m[0][0], m[0][1], m[0][2], m[0][3],
    m[1][0], m[1][1], m[1][2], m[1][3],
    m[2][0], m[2][1], m[2][2], m[2][3],
    m[3][0], m[3][1], m[3][2], m[3][3]
  );
}

/**
 * Normalizes an angle in degrees to the [0, 360) range.
 */
export function normalizeAngleDeg(deg: number): number {
  let d = Math.round(deg) % 360;
  if (d < 0) d += 360;
  return Object.is(d, -0) || d === 0 ? 0 : d;
}

/**
 * Extracts the in-plane rotation angle (in degrees, [0, 360)) from a 4x4 transform matrix.
 * In the truck reference frame (X=length, Y=width, Z=height), the orientation on the truck bed
 * corresponds to yaw around the vertical Z axis.
 */
export function extractOrientationAngleDeg(matrix: THREE.Matrix4): number {
  const rot = new THREE.Matrix4().extractRotation(matrix);
  const euler = new THREE.Euler().setFromRotationMatrix(rot, 'ZYX');
  return normalizeAngleDeg(THREE.MathUtils.radToDeg(euler.z));
}

/**
 * How far the lowest vertex of an orientation sits below its support plane (the surface the engine rests on the
 * dunnage). The engine's primary floor base is used; 0 for flat-bottomed parts and when the data is missing.
 */
export function getFloorDepth(orientation?: PartOrientation): number {
  const depth = orientation?.floors_bases?.[0]?.min_floor_depth;
  return typeof depth === 'number' && Number.isFinite(depth) ? depth : 0;
}

// Distance from a whole number of cells within which a z-level is float noise rather than a real offset.
const Z_LEVEL_GRID_TOLERANCE_FT = 1e-6;

/**
 * The engine judges a z-level with no tolerance: `customZLevel <= voxel + DunnageHeight` picks the first-layer
 * stability check and floor(customZLevel / voxel) the cell the part starts in (prepareOrientationLayerSetup). Noise
 * from the mesh math (0.5000000000000379 for a part on the floor) flips both, so a level within noise of a whole
 * number of cells is sent as exactly that many cells; any other level is left as it is (layer planes are not on the
 * grid in general). Built as k / cellsPerFoot: k * cellSize lands just below the boundary for some k.
 */
export function snapZLevelToCellGrid(zLevel: number, cellSize: number): number {
  if (!(cellSize > 0) || !Number.isFinite(zLevel)) {
    return zLevel;
  }
  const cells = Math.round(zLevel / cellSize);
  if (Math.abs(zLevel - cells * cellSize) > Z_LEVEL_GRID_TOLERANCE_FT) {
    return zLevel;
  }
  const cellsPerFoot = Math.round(1 / cellSize);
  return Math.abs(1 / cellSize - cellsPerFoot) < 1e-9 ? cells / cellsPerFoot : cells * cellSize;
}

/**
 * The engine's --positions-z-level is the layer (support) plane, not the mesh bottom.
 * The engine places the mesh bottom at zLevel - floorDepth - voxel (NestingAlgorithm.cpp prepareOrientationLayerSetup
 * and computePartTransformByPlacementPos), so the inverse is meshBottom + floorDepth + voxel (computeLayerStartAndEndZ).
 */
export function computePositionsZLevel(
  meshMinZ: number,
  orientation: PartOrientation | undefined,
  cellSize: number,
): number {
  return snapZLevelToCellGrid(meshMinZ + getFloorDepth(orientation) + cellSize, cellSize);
}

/**
 * Computes the placement transform matrix for a part when adopting a specific
 * engine-derived orientation (precomputed by StableMeshOrientationsFinder).
 *
 * Preserves the part's horizontal center (pivot position) and resting surface floor elevation,
 * aligning with the nesting algorithm's coordinate conventions.
 *
 * @param targetOri The target PartOrientation (containing transform_matrix and transformed_mesh_aabb)
 * @param pivotPosition The world-space center/pivot around which the part is oriented
 * @param floorZ The world-space elevation where the lowest vertex of the oriented part is placed
 * @param invParentWorld Optional parent world inverse matrix
 * @param part Optional Three.js Object3D to directly measure mesh geometry in Three.js coordinates
 * @param cellSize Optional engine grid cell size: the shift is rounded to whole cells because the orientation's
 *   translation is the engine grid origin, so the part lands on a cell corner and needs no later snap
 */
export function computeEngineOrientedPlacementMatrix(
  targetOri: PartOrientation,
  pivotPosition: THREE.Vector3,
  floorZ: number,
  invParentWorld?: THREE.Matrix4,
  part?: THREE.Object3D,
  cellSize?: number,
): THREE.Matrix4 {
  const targetMatrix = toMatrix4(targetOri.transform_matrix);
  const aabb = targetOri.transformed_mesh_aabb;

  let centerX = 0;
  let centerY = 0;
  let minZ = 0;
  let hasMeasured = false;

  if (part) {
    const measuredBox = measureBox(part, targetMatrix);
    if (!measuredBox.isEmpty()) {
      const center = measuredBox.getCenter(new THREE.Vector3());
      centerX = center.x;
      centerY = center.y;
      minZ = measuredBox.min.z;
      hasMeasured = true;
    }
  }

  if (!hasMeasured) {
    if (aabb && aabb.min && aabb.max) {
      centerX = (aabb.min[0] + aabb.max[0]) / 2;
      centerY = (aabb.min[1] + aabb.max[1]) / 2;
      minZ = aabb.min[2];
    } else {
      // Fallback: extract from targetMatrix translation if AABB is missing
      centerX = targetMatrix.elements[12];
      centerY = targetMatrix.elements[13];
      minZ = targetMatrix.elements[14];
    }
  }

  // Translation needed to place the center at pivotPosition and bottom at floorZ
  const snapToCell = (shift: number) => (cellSize ? Math.round(shift / cellSize) * cellSize : shift);
  const tx = snapToCell(pivotPosition.x - centerX);
  const ty = snapToCell(pivotPosition.y - centerY);
  const tz = floorZ - minZ;

  const translationMatrix = new THREE.Matrix4().makeTranslation(tx, ty, tz);
  const worldMatrix = translationMatrix.multiply(targetMatrix);

  if (invParentWorld) {
    return invParentWorld.clone().multiply(worldMatrix);
  }

  return worldMatrix;
}

/**
 * Computes an orientation transform matrix by rotating around the vertical (Z) axis
 * at the given pivot position, preserving contact elevation with the floor / dunnage.
 */
export function computeInPlaneOrientationMatrix(
  baseMatrix: THREE.Matrix4,
  pivotPosition: THREE.Vector3,
  deltaAngleRad: number,
  invParentWorld?: THREE.Matrix4,
  parentWorld?: THREE.Matrix4,
): THREE.Matrix4 {
  if (deltaAngleRad === 0) {
    return baseMatrix.clone();
  }

  const worldRotation = new THREE.Matrix4()
    .makeTranslation(pivotPosition.x, pivotPosition.y, pivotPosition.z)
    .multiply(new THREE.Matrix4().makeRotationZ(deltaAngleRad))
    .multiply(new THREE.Matrix4().makeTranslation(-pivotPosition.x, -pivotPosition.y, -pivotPosition.z));

  if (invParentWorld && parentWorld) {
    const localRotation = invParentWorld.clone().multiply(worldRotation).multiply(parentWorld);
    return localRotation.multiply(baseMatrix.clone());
  }

  return worldRotation.multiply(baseMatrix.clone());
}

/**
 * Checks if a part has an approximately square in-plane footprint (X and Y dimensions).
 * In the C++ nesting algorithm (computeTransformsByMeshAndDir), placeLongestDirAlongX is true for all
 * truck-placed steel parts, meaning 90-degree and 270-degree yaw orientations are ONLY generated
 * if the base footprint is square (|extentX - extentY| < tolerance).
 * Non-square parts only permit 180-degree yaw rotation (T1 and T2).
 */
export function isSquareFootprint(sizeX: number, sizeY: number, tolerance = 0.1): boolean {
  if (sizeX <= 0 || sizeY <= 0) return false;
  return Math.abs(sizeX - sizeY) <= tolerance || Math.abs(sizeX - sizeY) / Math.max(sizeX, sizeY) < 0.05;
}

export interface OrientationStepConfig {
  stepCount: number;
  stepAngleDeg: number;
}

/**
 * Returns orientation step configuration matching C++ nesting logic:
 * - Square footprint: 4 steps, 90 deg increments
 * - Elongated footprint: 2 steps, 180 deg increments
 */
export function getOrientationStepConfig(sizeX: number, sizeY: number): OrientationStepConfig {
  if (isSquareFootprint(sizeX, sizeY)) {
    return {
      stepCount: ORIENTATION_STEP_COUNT_SQUARE,
      stepAngleDeg: ORIENTATION_STEP_ANGLE_SQUARE_DEG,
    };
  }
  return {
    stepCount: ORIENTATION_STEP_COUNT_LONG,
    stepAngleDeg: ORIENTATION_STEP_ANGLE_LONG_DEG,
  };
}

/**
 * Computes the new orientation_index when stepping orientation in-plane,
 * matching C++ computeTransformsByMeshAndDir layout:
 * - For non-square bases: pair [2k, 2k+1] representing [0 deg, 180 deg]
 * - For square bases: group [4k, 4k+1, 4k+2, 4k+3] representing [0 deg, 180 deg, 90 deg, 270 deg]
 */
export function computeNextOrientationIndex(
  baseOrientationIndex: number | undefined,
  stepIndex: number,
  isSquare: boolean
): number | undefined {
  if (baseOrientationIndex === undefined || baseOrientationIndex < 0) {
    return undefined;
  }
  if (!isSquare) {
    // 2-step cycle: 0 -> baseOrientationIndex, 1 -> opposite in pair [2k, 2k+1]
    const baseGroup = Math.floor(baseOrientationIndex / 2) * 2;
    const isBaseFirst = baseOrientationIndex % 2 === 0;
    return stepIndex % 2 === 0
      ? (isBaseFirst ? baseGroup : baseGroup + 1)
      : (isBaseFirst ? baseGroup + 1 : baseGroup);
  } else {
    // 4-step cycle in C++ result vector order:
    // T1 (0 deg), T2 (180 deg), T3 (90 deg), T4 (270 deg)
    // Step index 0: 0 deg -> T1 (offset 0)
    // Step index 1: 90 deg -> T3 (offset 2)
    // Step index 2: 180 deg -> T2 (offset 1)
    // Step index 3: 270 deg -> T4 (offset 3)
    const baseGroup = Math.floor(baseOrientationIndex / 4) * 4;
    const stepToOffset = [0, 2, 1, 3];
    return baseGroup + (stepToOffset[stepIndex % 4] ?? 0);
  }
}




